import React, { FunctionComponent, useCallback } from 'react';
import { Pagination, Empty, Button } from 'antd';
import css from './CommentList.module.less';
import { useStore } from 'effector-react';
import post, { getComments, getReportComments } from '@stores/post';
import componentContent from './CommentList.content.json';
import useTranslate from '@hooks/useTranslate';
import Comment from '@components/Comment';
import { pageSizeOptions } from '../../constants/pagination';
import toCsvParser, { ITableParam } from '@helpers/toCsvParser';
import saveAs from '@helpers/fileSaver';
import { ArrowDownOutlined } from '@ant-design/icons';
import IComment from '@interfaces/IComment';

interface CommentListProps {}

const getCommentText = (comment: IComment): string => {
  if (Array.isArray(comment.content) && comment.content.length) {
    const contentText = comment.content
      .map((content) => content.text || content.gifUrl || '')
      .join('');

    if (contentText) {
      return contentText;
    }
  }

  return comment.text || comment.gifUrl || '';
};

const CommentList: FunctionComponent<CommentListProps> = () => {
  const content = useTranslate(componentContent);
  const { comments, item, pagination, preloaders } = useStore(post);

  const paginationChange = (current: number, pageSize?: number) => {
    getComments({ pagination: { current, pageSize } });
  };

  const downloadReport = useCallback(async () => {
    if (!item) {
      return;
    }

    const tableParams: ITableParam[] = [
      {
        title: 'ID',
        key: 'id',
      },
      {
        title: 'Text',
        key: 'text',
        valueRule: (value: string, comment: IComment) => getCommentText(comment),
      },
      {
        title: 'When Commented',
        key: 'whenCommented',
      },
      {
        title: 'Author Id',
        key: ['author', 'id'],
      },
      {
        title: 'Nickname',
        key: ['author', 'nickname'],
      },
      {
        title: 'Post ID',
        key: 'postId',
        valueRule: (value: string) => value || item?.id,
      },
      {
        title: 'Parent Comment ID',
        key: 'parentCommentId',
      },
      {
        title: 'Likes Count',
        key: 'likesCount',
      },
      {
        title: 'Spam',
        key: 'isBlocked',
      },
    ];
    const reportComments = await getReportComments(item.id);
    const parsedTable: string = toCsvParser<IComment>(tableParams, reportComments);
    saveAs(parsedTable, `comments-post-id-${item.id}.csv`);
  }, [item]);

  return (
    <div className={css.root}>
      <Button
        type='primary'
        icon={<ArrowDownOutlined />}
        onClick={downloadReport}
        className={css.downloadButton}
        loading={!!preloaders.getReportComments}
      >
        {content.download_report}
      </Button>
      {!comments.length ? (
        <Empty description={content.noComments} />
      ) : (
        <>
          {comments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
            />
          ))}
          <div className={css.pagination}>
            <Pagination
              {...pagination}
              showSizeChanger={true}
              showQuickJumper
              pageSizeOptions={pageSizeOptions}
              onChange={paginationChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CommentList;
